"use client";

import { useEffect, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@anima/ui/components/sheet";
import { Button } from "@anima/ui/components/button";
import { Badge } from "@anima/ui/components/badge";
import { ScrollArea } from "@anima/ui/components/scroll-area";
import { Separator } from "@anima/ui/components/separator";
import type { Route } from "next";
import Link from "next/link";
import { entityUrl } from "@/lib/routes";
import { getOptimizedMediaUrl } from "@/lib/content";
import { trpc } from "@/utils/trpc";

interface SelectedEntity {
  entityId: string;
  entitySlug: string;
  seriesSlug: string;
  asOfEntryId: string;
}

const TYPE_COLORS: Record<string, string> = {
  character: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  location: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  organization: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  item: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  concept: "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200",
  event: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  ability: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200",
  title: "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200",
  other: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
};

export function EntitySheet() {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<SelectedEntity | null>(null);

  const { data: entity, isLoading } = useQuery({
    ...trpc.entity.bySlugAsOf.queryOptions({
      seriesSlug: selected?.seriesSlug ?? "",
      entitySlug: selected?.entitySlug ?? "",
      asOfEntryId: selected?.asOfEntryId,
    }),
    enabled: !!selected,
  });

  const handleEntitySelect = useCallback((event: Event) => {
    const detail = (event as CustomEvent<SelectedEntity>).detail;
    setSelected(detail);
    setIsOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener("entity-select", handleEntitySelect);
    return () => window.removeEventListener("entity-select", handleEntitySelect);
  }, [handleEntitySelect]);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent side="right" className="flex w-100 flex-col gap-4 sm:max-w-100">
        {selected && (
          <>
            <SheetHeader className="flex-row items-center justify-between">
              <SheetTitle className="text-lg">
                {isLoading ? "Loading..." : (entity?.canonicalName ?? "Entity")}
              </SheetTitle>
              <Button variant="ghost" size="icon-sm" onClick={() => setIsOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </SheetHeader>

            <Separator />

            {isLoading ? (
              <div className="flex flex-1 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : entity ? (
              <ScrollArea className="flex-1">
                <div className="flex flex-col gap-4 pb-4">
                  {entity.imageR2Key && (
                    <img
                      src={getOptimizedMediaUrl(entity.imageR2Key, 800)}
                      alt={entity.canonicalName}
                      className="aspect-video w-full rounded-lg object-cover"
                    />
                  )}

                  <div className="flex items-center gap-2">
                    <Badge className={TYPE_COLORS[entity.type] ?? TYPE_COLORS.other}>
                      {entity.type}
                    </Badge>
                    <Link
                      href={
                        entityUrl(
                          selected.seriesSlug,
                          selected.entitySlug,
                          selected.asOfEntryId,
                        ) as Route
                      }
                      className="text-sm text-muted-foreground hover:underline"
                    >
                      View full page →
                    </Link>
                  </div>

                  {entity.shortSummary && (
                    <p className="text-sm leading-relaxed">{entity.shortSummary}</p>
                  )}

                  {entity.aliases.length > 0 && (
                    <div>
                      <p className="mb-1 text-xs font-medium text-muted-foreground">
                        Also known as
                      </p>
                      <p className="text-sm">{entity.aliases.join(", ")}</p>
                    </div>
                  )}

                  {entity.facts.length > 0 && (
                    <div>
                      <p className="mb-2 text-xs font-medium text-muted-foreground">
                        Facts ({entity.facts.length})
                      </p>
                      <div className="flex flex-col gap-2">
                        {entity.facts.slice(0, 10).map((fact) => (
                          <div key={fact.id} className="rounded-md border p-2">
                            <p className="text-xs text-muted-foreground">{fact.kind}</p>
                            <p className="text-sm">{fact.summary}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {entity.relations.length > 0 && (
                    <div>
                      <p className="mb-2 text-xs font-medium text-muted-foreground">
                        Relationships
                      </p>
                      <div className="flex flex-col gap-1.5">
                        {entity.relations.map((rel) => (
                          <div key={rel.id} className="rounded-md border p-2 text-sm">
                            <span className="font-medium">{rel.relation}</span>
                            {rel.fact && (
                              <p className="mt-1 text-xs text-muted-foreground">{rel.fact}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {entity.facts.length === 0 && entity.relations.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      No additional details available for this entity.
                    </p>
                  )}
                </div>
              </ScrollArea>
            ) : (
              <p className="text-sm text-muted-foreground">Entity not found.</p>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
